import { css } from "@emotion/react";
import { useState } from "react";
import * as yup from "yup";
import { CustomSpace } from "@store/space/types";
import { showNotification } from "@store/ui/uiThunk";
import { Form } from "@helpers";
import Button from "@components/base/button";
import { selectAllCustomSpaces } from "@store/space/spaceSelectors";
import useAppSelector from "@hooks/useAppSelector";
import useAppDispatch from "@hooks/useAppDispatch";
import Input from "@components/base/input";
import { useTranslation } from "react-i18next";
import l from "@locale";
interface PopOverProps {
  title?: string;
  space?: CustomSpace;
  submitTitle: string;
  handleSubmit: (space: any) => void;
  onCancel?: () => void;
  type: "create" | "update";
}

interface FormData {
  name: string;
  // Kept as the raw input value; an empty string means "floor unknown".
  floor: string;
}

const schema = yup.object().shape({
  name: yup.string().trim().required(l.t("customroom.create.nameRequired")),
  floor: yup
    .string()
    .trim()
    .matches(/^-?\d*$/, l.t("customroom.floorInvalid")),
});

// Empty input clears the floor, so send null instead of leaving it out.
const parseFloor = (value: string): number | null =>
  value.trim() === "" ? null : Number(value);
const CustomSpacePopOver = ({
  title,
  space,
  submitTitle,
  handleSubmit,
  onCancel,
  type,
}: PopOverProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [form, setForm] = useState<FormData>({
    name: space?.label || "",
    floor: space?.floor != null ? String(space.floor) : "",
  });
  const customSpaceLabels = useAppSelector(selectAllCustomSpaces).map(
    (space) => space.label,
  );

  const dispatchNotification = (message: string) => {
    dispatch(
      showNotification(
        {
          type: "error",
          message: message,
        },
        "add:customSpace",
      ),
    );
  };
  const handleSubmitClick = async () => {
    const error = await Form.validate(schema, form);
    if (type != "update" && customSpaceLabels.includes(form.name)) {
      dispatchNotification(t("customroom.create.nameInUse"));
      return;
    }
    if (error === null) {
      handleSubmit({
        label: form.name,
        floor: parseFloor(form.floor),
      });
      return;
    }
    dispatchNotification(Object.values(error)[0]);
  };
  const handleCancel = () => {
    setForm({
      name: "",
      floor: "",
    });
    if (onCancel) onCancel();
  };
  return (
    <>
      <div className={`modal ${title && "spacing"}`}>
        {title && (
          <h4 css={type === "update" && styles.modalHeader}>{title}</h4>
        )}

        <Input
          autoFocus
          label={t("common.name")}
          placeholder={t("common.giveName")}
          containerStyle={{ width: "420px" }}
          value={form.name}
          onChangeText={(text) => text && setForm({ ...form, name: text })}
          required={true}
        />
        <span css={styles.helper}>{t("customroom.tooltip")}</span>
        <Input
          label={t("common.floor")}
          placeholder={t("customroom.floorPlaceholder")}
          containerStyle={{ width: "420px", marginTop: "10px" }}
          inputType="number"
          value={form.floor}
          onChangeText={(text) => setForm({ ...form, floor: text })}
        />
        <div className="buttonContainer">
          <a
            css={styles.cancelButton}
            onClick={() => {
              close();
              handleCancel();
            }}>
            {t("common.cancel")}
          </a>
          <Button
            onClick={() => {
              handleSubmitClick();
              close();
            }}>
            {submitTitle}
          </Button>
        </div>
      </div>
      <style jsx>{`
        .modal {
          background-color: #fff;
          display: flex;
          postion: relative;
          flex-direction: column;
          align-items: flex-start;
          width: auto;
          min-width: 350px;
        }
        .spacing {
          padding: 20px;
        }
        .modal form {
          display: flex;
          flex-direction: column;
        }
        span {
          font-size: 0.65rem;
          padding-left: 2px;
          font-weight: 400;
        }
        .modal h4 {
          margin: 10px 0;
        }
        .modal label {
          padding-top: 10px;
          padding-left: 2px;
          margin-bottom: 2px;
          font-size: 0.9rem;
        }
        .buttonContainer {
          margin-top: 20px;
          width: 100%;
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
        }
      `}</style>
    </>
  );
};
const styles = {
  modal: css({
    display: "flex",
    borderRadius: "0",
  }),
  helper: css({
    paddingTop: "3px",
  }),
  modalHeader: css({
    fontSize: "1.5rem",
  }),
  cancelButton: css({
    marginLeft: "12px",
    borderBottom: "1px solid #2BB9F5",
  }),
};
export default CustomSpacePopOver;
